/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ChoiceGroup, getId, IChoiceGroupOption, IDropdownOption, Image, Label, Stack } from '@fluentui/react';
import { sum } from 'lodash';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CluTrainedModelStore } from 'src/clu/core/stores/CluTrainedModelStore';
import { BladeModel } from 'src/core/components/BladeModel';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { Portal } from 'src/core/utils/routingUtils';
import { ConfusionMatrixColorValueScale } from 'src/customText/appShell/modelDetailsPages/components/ConfusionMatrixColorValueScale';
import { CustomTextTrainedModelStore } from 'src/customText/core/stores/CustomTextTrainedModelStore';
import { getCellColor } from 'src/customText/core/utils/confusionMatrixUtils';
import { ModelEvaluationStore } from 'src/modules/modelDetails/stores/ModelEvaluationStore';
import { ConfusionMatrixFormat, ConfusionMatrixMode, ModelConfusionMatrix } from 'src/modules/modelGuidance/components/ConfusionMatrix';
import {
    getFormatDropDownToolTipText,
    getModelTypeLabel,
    getNormalizedFormatDropDownLabel,
    getRawCountFormatDropDownLabel,
    getSingleModelName,
    handleGetLabel,
    SchemaType
} from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';
import styled from 'styled-components';

export type ConfusionMatrixTabProps = {
    pivotName?: SchemaType;
    fileTypeMessage?: string;
    isEvaluationDataDisplayable: boolean;
    modelEvaluationStore: ModelEvaluationStore;
    initialConfusionMatrixFormat?: ConfusionMatrixFormat;
    trainedModel: CustomTextTrainedModelStore | CluTrainedModelStore;
    serviceName?: Portal['serviceName'];
    onOverviewTabClick: () => void;
    onRenderOptions?: () => React.ReactNode;
    onCellClick: (selectedLabels: string[]) => void;
};

const messages = defineMessages({
    documents: { id: 'modelGuidance.confusionMatrixTab.documents', defaultMessage: 'documents' },
    overviewTab: { id: 'modelGuidance.confusionMatrixTab.overviewTab', defaultMessage: 'Overview tab.' },
    allValuesView: { id: 'modelGuidance.confusionMatrixTab.allValuesView', defaultMessage: 'All values' },
    onlyErrorsView: { id: 'modelGuidance.confusionMatrixTab.onlyErrorsView', defaultMessage: 'only errors' },
    onlyMatchesView: { id: 'modelGuidance.confusionMatrixTab.onlyMatchesView', defaultMessage: 'only matches' },
    viewOptionsLabel: { id: 'modelGuidance.confusionMatrixTab.viewOptionsLabel', defaultMessage: 'Matrix view' },
    confusionMatrixExample: { id: 'modelGuidance.confusionMatrixTab.confusionMatrixExample', defaultMessage: 'Example:' },
    formatDropDownLabel: { id: 'modelGuidance.confusionMatrixTab.formatDropDownLabel', defaultMessage: 'Format of values' },
    learnMore: { id: 'modelGuidance.confusionMatrixTab.learnMore', defaultMessage: 'Learn how to read a confusion matrix.' },
    diagonalExampleTitle: { id: 'modelGuidance.confusionMatrixTab.diagonalExampleTitle', defaultMessage: 'Focus on the diagonal' },
    confusionMatrixTabDescription1: {
        id: 'modelGuidance.confusionMatrixTab.confusionMatrixTabDescription1',
        defaultMessage: `A confusion matrix is an N x N matrix used for evaluating the performance of a {modelType} model, where N is the number of target {labelType}. The matrix compares the actual target values with those predicted by the machine learning model to show how well the {modelType} model is performing and what kinds of errors it is making.`
    },
    confusionMatrixTabDescription2: {
        id: 'modelGuidance.confusionMatrixTab.confusionMatrixTabDescription2',
        defaultMessage: `All correct predictions are located in the diagonal of the table and errors are values outside of the diagonal. Other numbers in the row show where it was incorrectly predicted as other {labelType}. To see how to improve your model, check the recommendations in the`
    },
    confusionMatrixEmptyStateMsg: {
        id: 'modelGuidance.confusionMatrixTab.confusionMatrixEmptyStateMsg',
        defaultMessage:
            "Your model's training is in progress/has failed. The confusion matrix will appear here once training is completed successfully."
    },
    confusionMatrixErrorStateMsg: {
        id: 'modelGuidance.confusionMatrixTab.confusionMatrixErrorStateMsg',
        defaultMessage: 'Please make sure you include 10 or more examples ({fileType}).'
    },
    confusionMatrixNotAvailable: {
        id: 'modelGuidance.confusionMatrixTab.confusionMatrixNotAvailable',
        defaultMessage: 'Confusion matrix is not available for multi classification projects.'
    },
    diagonalExampleDescription: {
        id: 'modelGuidance.confusionMatrixTab.diagonalExampleDescription',
        defaultMessage: 'The diagonal boxes show where an {labelType} was correctly predicted as itself.'
    },
    diagonalExampleImageAltText: {
        id: 'modelGuidance.confusionMatrixTab.diagonalExampleImageAltText',
        defaultMessage: 'Confusion matrix with diagonal image.'
    },
    falseNegativeExampleTitle: {
        id: 'modelGuidance.confusionMatrixTab.falseNegativeExampleTitle',
        defaultMessage: 'False negatives (incorrect reject)'
    },
    falseNegativeExampleDescription: {
        id: 'modelGuidance.confusionMatrixTab.falseNegativeExampleDescription',
        defaultMessage:
            'False negatives are when a {fileType} is incorrectly rejected as not belonging to a particular {labelType}. They appear in any column square of the labeled class except for the diagonal.'
    },
    falseNegativeExampleImageAltText: {
        id: 'modelGuidance.confusionMatrixTab.falseNegativeExampleImageAltText',
        defaultMessage: 'Confusion matrix with false negatives image.'
    },
    falsePositiveExampleTitle: {
        id: 'modelGuidance.confusionMatrixTab.falsePositiveExampleTitle',
        defaultMessage: 'False positives (incorrect match)'
    },
    falsePositiveExampleDescription: {
        id: 'modelGuidance.confusionMatrixTab.falsePositiveExampleDescription',
        defaultMessage:
            'False positives are when text is incorrectly matched to a {labelType} to which it does not belong. The appear in any raw square of a predicted {labelType} except the diagonal.'
    },
    falsePositiveExampleImageAltText: {
        id: 'modelGuidance.confusionMatrixTab.falsePositiveExampleImageAltText',
        defaultMessage: 'Confusion matrix with false positives image.'
    }
});

const headerTheme = getScopedTheme('GlobalHeaderBlades');
const confusionMatrixColorTheme = getScopedTheme('ConfusionMatrixCellColors');

const BladeContainer = styled(Stack)({ padding: '18px 40px' });
const ConfusionMatrixImage = styled(Image)<{ width: string; margin: string }>(props => ({ width: props.width, margin: props.margin }));

export const ConfusionMatrixTab = (props: ConfusionMatrixTabProps) => {
    const { localize } = useLocalization();
    const choiceGroupIdRef = React.useRef(getId());
    const { isMediumOrGreater } = useResponsiveMode();
    const {
        pivotName,
        serviceName,
        trainedModel,
        modelEvaluationStore,
        isEvaluationDataDisplayable,
        initialConfusionMatrixFormat = 'normalized',
        fileTypeMessage = localize(messages.documents),
        onCellClick,
        onRenderOptions,
        onOverviewTabClick
    } = props;

    const [isConfusionMatrixBladeOpen, setIsConfusionMatrixBladeOpen] = React.useState(false);
    const [confusionMatrixMode, setConfusionMatrixMode] = React.useState<ConfusionMatrixMode>('all');
    const [confusionMatrixFormat, setConfusionMatrixFormat] = React.useState<ConfusionMatrixFormat>(initialConfusionMatrixFormat);

    const modelConfusionMatrix =
        confusionMatrixFormat === 'raw' ? modelEvaluationStore.confusionMatrix : modelEvaluationStore.normalizedConfusionMatrix;

    React.useEffect(() => {
        const columnIcon = document.getElementById(choiceGroupIdRef.current)?.getElementsByTagName('svg')[0];
        if (columnIcon) {
            columnIcon.setAttribute('width', '30');
            columnIcon.setAttribute('height', '30');
            columnIcon.setAttribute('stroke', 'black');
            columnIcon.setAttribute('stroke-width', '0.5');
        }
    }, []);

    const isNoneAndNoneCell = (index1: number, index2: number) =>
        index1 === index2 &&
        modelEvaluationStore.performanceMetricsBreakdown[index1]?.name === LuisConstants.Clu.NoneLabelInConfusionMatrix;

    const metricsLength = React.useMemo(() => modelEvaluationStore.performanceMetricsBreakdown.length, [
        modelEvaluationStore.performanceMetricsBreakdown
    ]);

    const { allViewIndices, onlyErrorsIndices, onlyMatchesIndices } = React.useMemo(
        () => ({
            allViewIndices: modelEvaluationStore.normalizedConfusionMatrix ? [...Array(metricsLength).keys()] : [],
            onlyErrorsIndices: modelEvaluationStore.normalizedConfusionMatrix
                ? [...Array(metricsLength).keys()].filter(
                      index =>
                          !(
                              (modelEvaluationStore.normalizedConfusionMatrix[index][index] === 1 &&
                                  sum(modelEvaluationStore.normalizedConfusionMatrix[index]) === 1) ||
                              (sum(modelEvaluationStore.normalizedConfusionMatrix[index]) === 0 &&
                                  modelEvaluationStore.normalizedConfusionMatrix.reduce((acc, curr) => acc + curr[index], 0) === 0)
                          )
                  )
                : [],
            onlyMatchesIndices: modelEvaluationStore.normalizedConfusionMatrix
                ? [...Array(metricsLength).keys()].filter(
                      index =>
                          modelEvaluationStore.normalizedConfusionMatrix[index][index] === 1 &&
                          sum(modelEvaluationStore.normalizedConfusionMatrix[index]) === 1
                  )
                : []
        }),
        [modelEvaluationStore.normalizedConfusionMatrix]
    );
    const metricsIndicesToShow =
        confusionMatrixMode === 'errors' ? onlyErrorsIndices : confusionMatrixMode === 'matches' ? onlyMatchesIndices : allViewIndices;

    const maximumNumberInConfusionMatrix = React.useMemo(
        () =>
            modelConfusionMatrix?.reduce(
                (acc, curr, index) =>
                    !metricsIndicesToShow.includes(index) || isNoneAndNoneCell(index, index) ? acc : Math.max(acc, Math.max(...curr)),
                0
            ),
        [modelConfusionMatrix, metricsIndicesToShow]
    );

    const confusionMatrixCellColors = React.useMemo(
        () =>
            modelConfusionMatrix?.map(row => row.map(val => getCellColor(val / maximumNumberInConfusionMatrix, confusionMatrixColorTheme))),
        [modelConfusionMatrix, maximumNumberInConfusionMatrix]
    );

    const onRenderLabel = (label: string, tooltipContent: string | React.ReactNode) => (
        <Label>
            {label}
            <CalloutButton isDarkMode={false} content={tooltipContent} />
        </Label>
    );

    const getBladeContent = () => (
        <Stack tokens={{ childrenGap: 20 }}>
            {confusionMatrixExamples.map(example => (
                <Stack tokens={{ childrenGap: 8 }}>
                    <LuisText fontWeight={600} as="h2">
                        {example.title}
                    </LuisText>
                    <LuisText>{example.description}</LuisText>
                    <LuisText>{localize(messages.confusionMatrixExample)}</LuisText>
                    <ConfusionMatrixImage
                        imageFit={1}
                        width={example.width}
                        src={example.imageSrc}
                        margin={example.margin}
                        alt={example.imageAltText}
                    />
                </Stack>
            ))}
        </Stack>
    );

    const confusionMatrixExamples = [
        {
            width: '360px',
            margin: 'auto',
            title: localize(messages.diagonalExampleTitle),
            description: localize(messages.diagonalExampleDescription, { labelType: localize(getSingleModelName(pivotName, serviceName)) }),
            imageAltText: localize(messages.diagonalExampleImageAltText),
            imageSrc: getAssetsUrl('assets/images/customText/focusOnTheDiagonalConfusionMatrix.svg')
        },
        {
            width: '100px',
            margin: 'initial',
            title: localize(messages.falseNegativeExampleTitle),
            imageAltText: localize(messages.falseNegativeExampleImageAltText),
            imageSrc: getAssetsUrl('assets/images/customText/falseNegativesConfusionMatrix.svg'),
            description: localize(messages.falseNegativeExampleDescription, {
                fileType: fileTypeMessage,
                labelType: localize(getSingleModelName(pivotName, serviceName))
            })
        },
        {
            width: '360px',
            margin: 'auto',
            title: localize(messages.falsePositiveExampleTitle),
            description: localize(messages.falsePositiveExampleDescription, {
                labelType: localize(getSingleModelName(pivotName, serviceName))
            }),
            imageAltText: localize(messages.falsePositiveExampleImageAltText),
            imageSrc: getAssetsUrl('assets/images/customText/falsePositivesConfusionMatrix.svg')
        }
    ];

    const blades: BladeModel[] = [
        {
            width: 600,
            key: 'confusionMatrixBlade',
            header: (
                <LuisText styles={{ root: { paddingLeft: 8 } }} fontWeight={600} variant="xLarge">
                    {localize(messages.learnMore)}
                </LuisText>
            ),
            content: (
                <div tabIndex={0}>
                    <BladeContainer tokens={{ childrenGap: 12 }}>{getBladeContent()}</BladeContainer>
                </div>
            )
        }
    ];

    const viewOptions: IChoiceGroupOption[] = [
        { key: 'all', role: 'radio', text: localize(messages.allValuesView), iconProps: { iconName: 'TimelineMatrixView' } },
        { key: 'errors', role: 'radio', text: localize(messages.onlyErrorsView), iconProps: { iconName: 'RemoveFilter' } },
        { key: 'matches', role: 'radio', text: localize(messages.onlyMatchesView), iconProps: { iconName: 'Column' } }
    ];

    const formatOptions: IDropdownOption[] = [
        { key: 'normalized', text: localize(getNormalizedFormatDropDownLabel(pivotName, serviceName)) },
        { key: 'raw', text: localize(getRawCountFormatDropDownLabel(pivotName, serviceName)) }
    ];

    return (
        <>
            <Stack
                verticalFill
                tokens={{ childrenGap: 15 }}
                styles={{ root: { margin: '20px 0' } }}
                data-automation-id="testSetConfusionMatrixTab">
                <LuisText>
                    {localize(messages.confusionMatrixTabDescription1, {
                        modelType: localize(getModelTypeLabel(pivotName, serviceName)),
                        labelType: localize(handleGetLabel(pivotName, serviceName, false))
                    })}
                </LuisText>

                <LuisText>
                    {localize(messages.confusionMatrixTabDescription2, {
                        labelType: localize(handleGetLabel(pivotName, serviceName, false))
                    })}
                    &nbsp;
                    <ExternalLink onClick={onOverviewTabClick}>{localize(messages.overviewTab)}</ExternalLink>
                </LuisText>

                <ExternalLink role="link" onClick={() => setIsConfusionMatrixBladeOpen(true)}>
                    {localize(messages.learnMore)}
                </ExternalLink>

                {onRenderOptions && onRenderOptions()}

                <Stack horizontal={isMediumOrGreater} verticalFill tokens={{ childrenGap: 37 }}>
                    <ChoiceGroup
                        options={viewOptions}
                        defaultSelectedKey="all"
                        id={choiceGroupIdRef.current}
                        label={localize(messages.viewOptionsLabel)}
                        onChange={(_, opt) => setConfusionMatrixMode(opt.key as ConfusionMatrixMode)}
                    />

                    <LuisDropdown
                        isSearchEnabled={false}
                        options={formatOptions}
                        styles={{ root: { width: isMediumOrGreater ? '300px' : '90%' } }}
                        selectedKey={confusionMatrixFormat}
                        ariaLabel={localize(messages.formatDropDownLabel)}
                        onChange={(_, item) => setConfusionMatrixFormat(item.key as ConfusionMatrixFormat)}
                        onRenderLabel={() =>
                            onRenderLabel(
                                localize(messages.formatDropDownLabel),
                                localize(getFormatDropDownToolTipText(serviceName, pivotName))
                            )
                        }
                    />
                </Stack>

                {isEvaluationDataDisplayable ? (
                    <>
                        <ConfusionMatrixColorValueScale maxNumber={maximumNumberInConfusionMatrix ?? 1} />
                        <ModelConfusionMatrix
                            onCellClick={onCellClick}
                            mode={confusionMatrixMode}
                            allViewIndices={allViewIndices}
                            onlyErrorsIndices={onlyErrorsIndices}
                            onlyMatchesIndices={onlyMatchesIndices}
                            modelConfusionMatrix={modelConfusionMatrix}
                            confusionMatrixCellColors={confusionMatrixCellColors}
                            performanceMetricsBreakdown={modelEvaluationStore.performanceMetricsBreakdown}
                        />
                    </>
                ) : (
                    <EmptyStateView
                        imageSrc={getAssetsUrl(`assets/images/customText/emptyConfusionMatrixIllustration.svg`)}
                        message={localize(trainedModel ? messages.confusionMatrixErrorStateMsg : messages.confusionMatrixEmptyStateMsg, {
                            fileType: fileTypeMessage
                        })}
                    />
                )}
            </Stack>

            <StackBladeHost
                blades={blades}
                topSpacing={headerTheme.topSpacing}
                isOpen={isConfusionMatrixBladeOpen}
                onDismiss={() => setIsConfusionMatrixBladeOpen(false)}
                onLastBladeDismissed={() => setIsConfusionMatrixBladeOpen(false)}
            />
        </>
    );
};
